import { useQuery } from "@tanstack/react-query";
import type { SalesGranularity } from "@/modules/reports/domain/contracts";
import { reportsApi } from "./client";

export const reportKeys = {
  timeseries: (p: {
    from: string;
    to: string;
    granularity: SalesGranularity;
  }) => ["reports", "sales-timeseries", p.from, p.to, p.granularity] as const,
  byChannel: (p: { from: string; to: string }) =>
    ["reports", "sales-by-channel", p.from, p.to] as const,
  bySocialSource: (p: { from: string; to: string }) =>
    ["reports", "sales-by-social-source", p.from, p.to] as const,
  byCustomerGroup: (p: { from: string; to: string }) =>
    ["reports", "sales-by-customer-group", p.from, p.to] as const,
  customerGroupStats: () =>
    ["reports", "customer-group-stats"] as const,
};

export function useSalesTimeseriesQuery(params: {
  from: string;
  to: string;
  granularity: SalesGranularity;
  enabled?: boolean;
}) {
  const enabled =
    params.enabled !== false && Boolean(params.from.length && params.to.length);
  return useQuery({
    queryKey: reportKeys.timeseries(params),
    queryFn: () => reportsApi.salesTimeseries(params),
    enabled,
  });
}

export function useSalesByChannelQuery(params: {
  from: string;
  to: string;
  enabled?: boolean;
}) {
  const enabled =
    params.enabled !== false && Boolean(params.from.length && params.to.length);
  return useQuery({
    queryKey: reportKeys.byChannel(params),
    queryFn: () => reportsApi.salesByChannel(params),
    enabled,
  });
}

export function useSalesBySocialSourceQuery(params: {
  from: string;
  to: string;
  enabled?: boolean;
}) {
  const enabled =
    params.enabled !== false && Boolean(params.from.length && params.to.length);
  return useQuery({
    queryKey: reportKeys.bySocialSource(params),
    queryFn: () => reportsApi.salesBySocialSource(params),
    enabled,
  });
}

export function useSalesByCustomerGroupQuery(params: {
  from: string;
  to: string;
  enabled?: boolean;
}) {
  const enabled =
    params.enabled !== false && Boolean(params.from.length && params.to.length);
  return useQuery({
    queryKey: reportKeys.byCustomerGroup(params),
    queryFn: () => reportsApi.salesByCustomerGroup(params),
    enabled,
  });
}

export function useCustomerGroupStatsQuery() {
  return useQuery({
    queryKey: reportKeys.customerGroupStats(),
    queryFn: () => reportsApi.customerGroupStats(),
    staleTime: 5 * 60_000,
  });
}
